import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Table, TableBody, TableContainer, TableHead, TableRow, Paper } from "@mui/material";

import { SmallCell, MediumCell, LargeCell } from "../style/tablecell";
import { TypographyNomalBold } from '../style/typography';
import SelectField from '../components/select_field';
import { validationSchemaCheckReult } from '../func/validation_check';
import { CustomTextFieldElementCheckResult, CustomTextFieldElementCheckResultLarge } from '../components/CustomTextFieldElement';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { normalizeIntensity, handleUpdateCheckResult, shouldValidateSituation } from '../func/util';

const RESULTS_OPTIONS = [
    { value: 0, label: '　' },
    { value: 1, label: '無' },
    { value: 2, label: '有' }
];

export default function CheckResult({ checkResult, setCheckResult, isReadOnly, facilityDams }) {
    const { control, getValues, setValue, trigger, clearErrors, setError } = useForm({
        defaultValues: { checkResults: checkResult },
        resolver: yupResolver(validationSchemaCheckReult),
        mode: "onBlur",
        context: { facilityDams }
    });

    // 入力モードでは、該当コードは初期時にバリデーション表示
    useEffect(() => {
        if (!isReadOnly) {
            checkResult.forEach((row, index) => {
                // 特定のuuid（例：facilityDams）かつsituationが空の場合にエラー設定
                if (shouldValidateSituation(row.facility_uuid, row.intensity, facilityDams)) {
                    if (!row.situation || row.situation.trim() === '') {
                        setError(`checkResults.${index}.situation`, {
                            type: "manual",
                            message: "必須項目です。状況を入力してください。",
                        });
                    }
                }
            });
        }
    }, []);

    const handleChange = (index, field, value) => {
        const fieldName = field.includes('.') ? field.split('.').pop() : field;

        let normalizedValue = value;
        if (fieldName === "intensity") {
            normalizedValue = normalizeIntensity(value);
        }

        const currentResults = getValues("checkResults");
        const updatedRow = {
            ...currentResults[index],
            [fieldName]: normalizedValue ?? false,
        };

        // 'has_anomary' が変更された場合のロジック
        if (fieldName === "has_anomary") {
            if (normalizedValue === 0 || normalizedValue === 1) {
                updatedRow.anomaly_facility_name = "";
                updatedRow.situation = "";
                if (!shouldValidateSituation(updatedRow.facility_uuid, updatedRow.intensity, facilityDams)) {
                    clearErrors(`checkResults.${index}.situation`);
                }
                clearErrors(`checkResults.${index}.anomaly_facility_name`);
            }
        }

        // フォームの状態を更新
        handleUpdateCheckResult(setCheckResult, updatedRow, facilityDams);
        setValue(`checkResults.${index}`, updatedRow);

        trigger(`checkResults.${index}.${fieldName}`);

        // 震度更新時に明示的に状況のバリデーションをトリガー
        if (fieldName === "intensity") {
            trigger(`checkResults.${index}.situation`);
        }

        // 'has_anomary'更新時に、2に設定されたときのみ実行
        if (fieldName === "has_anomary") {
            if (normalizedValue === 2) {
                trigger(`checkResults.${index}.anomaly_facility_name`);
                trigger(`checkResults.${index}.situation`);
            }
        };
    };

    const [tooltipOpen, setTooltipOpen] = useState(false);

    const handleTooltipToggle = () => {
        setTooltipOpen((prev) => !prev);
    };

    const handleTooltipClose = () => {
        setTooltipOpen(false);
    };

    const renderSelectField = (row, index, field, label) => (
        isReadOnly ? (
            RESULTS_OPTIONS.find(option => option.value === row[field])?.label || ""
        ) : (
            <SelectField
                name={`checkResults.${index}.${field}`}
                control={control}
                label={label}
                options={RESULTS_OPTIONS}
                row={row}
                index={index}
                handleChange={handleChange}
                facilityDams={facilityDams}
            />
        )
    );

    const renderTextFieldElement = (row, index, field, label) => (
        isReadOnly ? (
            row[field] || "—"
        ) : (
            <CustomTextFieldElementCheckResult
                name={field}
                index={index}
                control={control}
                label={label}
                onChange={(e) => handleChange(index, field, e.target.value)}
            />
        )
    );

    return (
        <TableContainer component={Paper} sx={{ maxHeight: 800, overflowX: "auto" }}>
            <Table stickyHeader sx={{ tableLayout: "auto", minWidth: "100%" }}>
                <TableHead sx={{ height: 130 }}>
                    <TableRow>
                        <SmallCell><TypographyNomalBold>No</TypographyNomalBold></SmallCell>
                        <SmallCell><TypographyNomalBold>ダム・発電所</TypographyNomalBold></SmallCell>
                        <MediumCell><TypographyNomalBold>最大震度</TypographyNomalBold></MediumCell>
                        <SmallCell><TypographyNomalBold>設備異常有無</TypographyNomalBold></SmallCell>
                        <SmallCell><TypographyNomalBold>第3者影響有無</TypographyNomalBold></SmallCell>
                        <SmallCell><TypographyNomalBold>報道対応有無</TypographyNomalBold></SmallCell>
                        <LargeCell><TypographyNomalBold>異常有設備名</TypographyNomalBold></LargeCell>
                        <LargeCell>
                            <ClickAwayListener onClickAway={handleTooltipClose}>
                                <span style={{ display: "flex", alignItems: "center" }}>
                                    <Tooltip
                                        open={tooltipOpen}
                                        onClose={handleTooltipClose}
                                        disableFocusListener
                                        disableHoverListener
                                        disableTouchListener
                                        title={
                                            <>
                                                小水力は、日常巡視で確認予定など、<br />
                                                ダムは、〇:〇　ITVで確認した結果、異常なし
                                            </>
                                        }
                                        arrow
                                        placement="bottom-start"
                                    >
                                        <InfoOutlinedIcon
                                            fontSize="small"
                                            onClick={handleTooltipToggle}
                                            sx={{ marginRight: 0.5, cursor: "pointer", color: "#0A7CB7" }}
                                        />
                                    </Tooltip>
                                    <TypographyNomalBold>状況及び臨時点検実施状況等</TypographyNomalBold>
                                </span>
                            </ClickAwayListener>
                        </LargeCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {checkResult
                        .filter((row) => {
                            if (isReadOnly) {
                                return shouldValidateSituation(row.facility_uuid, row.intensity, facilityDams);
                            }
                            return true;
                        })
                        .map((row, index) => (
                            <TableRow key={row.facility_uuid || `${index}-${row.facility_name || 'no_facility_name'}`}>
                                <SmallCell>{index + 1}</SmallCell>
                                <SmallCell>{row.facility_name}</SmallCell>
                                <MediumCell>{renderTextFieldElement(row, index, "intensity", "最大震度")}</MediumCell>
                                <SmallCell>{renderSelectField(row, index, "has_anomary", "設備異常有無")}</SmallCell>
                                <SmallCell>{renderSelectField(row, index, "has_impact", "第3者影響有無")}</SmallCell>
                                <SmallCell>{renderSelectField(row, index, "has_media_response", "報道対応有無")}</SmallCell>
                                <LargeCell>{renderTextFieldElement(row, index, "anomaly_facility_name", "異常有設備名")}</LargeCell>
                                <LargeCell>{renderTextFieldElement(row, index, "situation", "状況")}</LargeCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}